import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ClipboardPen, Save, History, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAMPS = [
  { key: 'comportement', label: 'Comportement & participation', cats: ['positif', 'vigilance'] },
  { key: 'assiduite', label: 'Assiduité & ponctualité', cats: ['info', 'vigilance'] },
  { key: 'resultats', label: 'Résultats & progression', cats: ['progression'] },
  { key: 'recommandations', label: 'Recommandations pour la suite', cats: ['alerte', 'progression'] },
];
const EMPTY = { comportement: '', assiduite: '', resultats: '', recommandations: '' };
const appendPhrase = (t, phrase) => { const s = (t || '').trim(); return s ? `${s}${/[.!?]$/.test(s) ? '' : '.'} ${phrase}` : phrase; };

export const AppreciationPanel = ({ dossierId, candidatId, canEdit }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [chips, setChips] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showHisto, setShowHisto] = useState(false);

  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/appreciation`).then(r => { setData(r.data); setForm({ ...EMPTY, ...Object.fromEntries(CHAMPS.map(c => [c.key, r.data[c.key] || ''])) }); }).catch(() => setData({}));
    axios.get(`${API_URL}/listes/chips-observation`).then(r => setChips(r.data.filter(c => c.actif !== false))).catch(() => {});
  }, [dossierId, candidatId]);

  const dirty = data && CHAMPS.some(c => (data[c.key] || '') !== form[c.key]);
  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/appreciation`, form); setData(r.data); toast.success('Appréciation enregistrée'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  if (!data) return <Card className="border-0 shadow-sm"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></CardContent></Card>;

  return (
    <Card className="border-0 shadow-sm" data-testid="appreciation-panel">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><ClipboardPen className="w-4 h-4 text-indigo-500" /> Appréciation générale du formateur</h2>
            <p className="text-xs text-slate-400 mt-0.5">Avis global sur le candidat, distinct des observations journalières.{data.updated_at && <> Dernière mise à jour : {data.auteur_nom} le {format(parseISO(data.updated_at), 'dd/MM/yyyy HH:mm')}</>}</p>
          </div>
          <div className="flex items-center gap-2">
            {(data.historique || []).length > 0 && <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowHisto(!showHisto)} data-testid="appreciation-histo-btn"><History className="w-3.5 h-3.5 mr-1" /> Historique ({data.historique.length})</Button>}
            {canEdit && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={!dirty || saving} onClick={save} data-testid="appreciation-save-btn"><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</Button>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CHAMPS.map(ch => {
            const list = chips.filter(c => ch.cats.includes(c.categorie));
            return (
              <div key={ch.key} className="space-y-1" data-testid={`appreciation-field-${ch.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-slate-600">{ch.label}</p>
                  {canEdit && list.length > 0 && (
                    <select value="" onChange={(e) => { const chip = list.find(c => c.id === e.target.value); if (chip) setForm({ ...form, [ch.key]: appendPhrase(form[ch.key], chip.phrase) }); }}
                      className="h-6 max-w-[220px] text-[11px] rounded border border-slate-200 bg-white text-slate-600 px-1" data-testid={`appreciation-select-${ch.key}`}>
                      <option value="">+ Insérer une phrase type…</option>
                      {list.map(chip => <option key={chip.id} value={chip.id} disabled={(form[ch.key] || '').toLowerCase().includes(chip.phrase.toLowerCase())}>{chip.libelle}</option>)}
                    </select>
                  )}
                </div>
                <Textarea rows={2} value={form[ch.key]} disabled={!canEdit} onChange={(e) => setForm({ ...form, [ch.key]: e.target.value })} className="text-sm" placeholder={canEdit ? 'Saisir ou insérer une phrase type…' : '—'} data-testid={`appreciation-textarea-${ch.key}`} />
              </div>
            );
          })}
        </div>
        {showHisto && (
          <div className="border-t pt-3 space-y-2" data-testid="appreciation-historique">
            {data.historique.map((h, i) => (
              <div key={i} className="text-xs bg-slate-50 rounded-lg p-3">
                <p className="text-slate-400 mb-1">{h.auteur_nom} — {h.date ? format(parseISO(h.date), 'dd/MM/yyyy HH:mm') : ''}</p>
                {CHAMPS.filter(c => h[c.key]).map(c => <p key={c.key} className="text-slate-700"><span className="font-medium">{c.label} :</span> {h[c.key]}</p>)}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
